// view.js – frontend render pour le bloc brblocks/documents
(function () {
  "use strict";

  const ready = (fn) => {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  };

  ready(() => {
    const blocks = document.querySelectorAll(".wp-block-brblocks-documents");
    if (!blocks.length) return;

    blocks.forEach((block) => {
      const container = block.querySelector(".documents-container");
      const docsDataEl = block.querySelector(".documents-data");
      const initialMode = (block.dataset.viewmode || "grid").toLowerCase();
      const columns = block.dataset.columns || 3;
      const showToggle = block.dataset.showtoggle === "true";
      const showSearch = block.dataset.showsearch === "true";
      const searchPlaceholder = block.dataset.searchplaceholder || "Search documents…";
      const showViewer = block.dataset.showviewer === "true";
      if (!container || !docsDataEl) return;

      // Parse data
      let documents = [];
      try {
        documents = JSON.parse(docsDataEl.dataset.documents || "[]");
      } catch (e) {
        console.error("[brblocks/documents] Invalid JSON in data-documents", e);
        return;
      }
      if (!documents.length) return;

      let currentQuery = "";
      const getLangFromHash = () => {
        const match = window.location.hash.match(/^#\/([a-z]{2})$/i);
        return match ? match[1].toLowerCase() : "";
      };
      let currentLang = getLangFromHash();

      // ── Helpers ──────────────────────────────────────────────────────────

      const detectType = (url, fallback = "file") => {
        if (!url) return fallback;
        const clean = url.split("#")[0].split("?")[0];
        const hasExt = /\.[a-z0-9]+$/i.test(clean);
        if (!hasExt || /\/$/.test(clean) || /\/folders?\//i.test(clean)) return "folder";
        const ext = (clean.split(".").pop() || "").toLowerCase();
        if (/pdf/.test(ext)) return "pdf";
        if (/docx?$/.test(ext)) return "doc";
        if (/xlsx?$/.test(ext)) return "xls";
        if (/csv/.test(ext)) return "csv";
        if (/^pptx?$/.test(ext)) return "ppt";
        return fallback;
      };

      const getPreviewType = (url, type) => {
        if (!url || url === "#") return "none";
        const ext = (url.split("?")[0].split("#")[0].split(".").pop() || "").toLowerCase();
        if (type === "pdf" || ext === "pdf") return "pdf";
        if (/^html?$/.test(ext)) return "html";
        if (/^(jpe?g|png|gif|webp|svg)$/.test(ext)) return "image";
        if (/^(docx?|pptx?|xlsx?)$/.test(ext) || type === "doc" || type === "xls" || type === "ppt") return "office";
        return "none";
      };

      const iconSVG = (typeRaw) => {
        const type = (typeRaw || "file").toLowerCase();
        if (type === "folder") {
          return `<svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
            <rect x="26" y="8" width="24" height="22" rx="3" fill="#fff" stroke="#009cd6" stroke-width="2.2" stroke-linejoin="round"/>
            <path d="M4 24a5 5 0 0 1 5-5h15l5 5.5h22a5 5 0 0 1 5 5V52a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V24z"
                  fill="#fff" stroke="#009cd6" stroke-width="2.5" stroke-linejoin="round"/>
          </svg>`;
        }
        const colors = {
          pdf:  { badge: "#E53935", text: "PDF" },
          doc:  { badge: "#1E88E5", text: "DOC" },
          xls:  { badge: "#2E7D32", text: "XLS" },
          csv:  { badge: "#6D4C41", text: "CSV" },
          ppt:  { badge: "#D84315", text: "PPT" },
          file: { badge: "#009cd6", text: "FILE" },
        };
        const c = colors[type] || colors.file;
        return `<svg viewBox="0 0 64 64" aria-hidden="true" focusable="false">
          <path d="M14 6h25l12 12v36a6 6 0 0 1-6 6H14a6 6 0 0 1-6-6V12a6 6 0 0 1 6-6z" fill="#F4F6F8" stroke="#009cd6"/>
          <path d="M39 6v10a6 6 0 0 0 6 6h10L39 6z" fill="#e5f5fb"/>
          <rect x="18" y="26" width="28" height="2" rx="1.5" fill="#009cd6"/>
          <rect x="18" y="32" width="24" height="2" rx="1.5" fill="#009cd6"/>
          <rect x="18" y="38" width="20" height="2" rx="1.5" fill="#009cd6"/>
          <rect x="16" y="51" width="28" height="12" rx="6" fill="${c.badge}"/>
          <text x="30" y="61" text-anchor="middle" font-size="8.5"
                font-family="system-ui, -apple-system, Segoe UI, Roboto, Arial"
                fill="#FFFFFF" font-weight="700" letter-spacing=".5">${c.text}</text>
        </svg>`;
      };

      const iconDownload = `<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" style="flex-shrink:0">
        <path d="M12 16l-5-5h3V4h4v7h3l-5 5z" fill="currentColor"/>
        <path d="M5 19h14v2H5z" fill="currentColor"/>
      </svg>`;

      const iconOpen = `<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" style="flex-shrink:0">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <polyline points="15 3 21 3 21 9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>`;

      const iconTable = `<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">
        <g fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
          <rect x="2" y="3" width="16" height="14" rx="2"/>
          <path d="M2 7.5h16"/><path d="M2 12h16"/><path d="M8 7.5v8.5"/>
        </g>
      </svg>`;
      const iconCards = `<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">
        <g fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
          <rect x="2" y="2" width="6.5" height="6.5" rx="1.5"/>
          <rect x="11.5" y="2" width="6.5" height="6.5" rx="1.5"/>
          <rect x="2" y="11.5" width="6.5" height="6.5" rx="1.5"/>
          <rect x="11.5" y="11.5" width="6.5" height="6.5" rx="1.5"/>
        </g>
      </svg>`;

      const getFilteredDocs = () => {
        let filtered = documents;
        const lang = currentLang || "en";
        filtered = filtered.filter((doc) => {
          const docLang = (doc.lang || "").toLowerCase();
          return !docLang || docLang === lang;
        });
        if (currentQuery) {
          filtered = filtered.filter((doc) => {
            const title = (doc.title || "").toLowerCase();
            const type = (doc.type || "").toLowerCase();
            const desc = (doc.description || "").toLowerCase();
            return title.includes(currentQuery) || type.includes(currentQuery) || desc.includes(currentQuery);
          });
        }
        return filtered;
      };

      const createDocElement = (doc, className) => {
        const type = (doc.type || detectType(doc.link)).toLowerCase();
        const newTab = doc.openInNewTab !== false;
        const el = document.createElement("a");
        el.className = className;
        el.href = doc.link || "#";
        if (newTab) { el.target = "_blank"; el.rel = "noopener noreferrer"; }

        const iconSpan = document.createElement("span");
        if (doc.customIcon) {
          iconSpan.className = "doc-icon doc-custom";
          const img = document.createElement("img");
          img.src = doc.customIcon; img.alt = ""; img.className = "doc-custom-icon";
          iconSpan.appendChild(img);
        } else {
          iconSpan.className = `doc-icon doc-${type}`;
          iconSpan.innerHTML = iconSVG(type);
        }

        const titleSpan = document.createElement("span");
        titleSpan.className = "doc-title";
        titleSpan.innerHTML = doc.title || "";

        el.appendChild(iconSpan);
        el.appendChild(titleSpan);

        if (doc.description) {
          const descSpan = document.createElement("span");
          descSpan.className = "doc-description";
          descSpan.innerHTML = doc.description;
          el.appendChild(descSpan);
        }
        return el;
      };

      const createNoResults = () => {
        const el = document.createElement("div");
        el.className = "documents-no-results";
        el.textContent = "No documents found.";
        return el;
      };

      // ── Viewer mode ───────────────────────────────────────────────────────
      if (showViewer) {
        const layout = document.createElement("div");
        layout.className = "documents-viewer-layout";

        // Left sidebar
        const sidebar = document.createElement("div");
        sidebar.className = "documents-viewer-sidebar";

        let searchInput = null;
        if (showSearch) {
          const searchWrapper = document.createElement("div");
          searchWrapper.className = "documents-search viewer-search";
          searchWrapper.innerHTML = `<svg class="documents-search__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="2"/>
            <path d="M16 16l4.5 4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>`;
          searchInput = document.createElement("input");
          searchInput.type = "search";
          searchInput.className = "documents-search__input";
          searchInput.placeholder = searchPlaceholder;
          searchInput.setAttribute("aria-label", searchPlaceholder);
          searchWrapper.appendChild(searchInput);
          sidebar.appendChild(searchWrapper);
        }

        const listContainer = document.createElement("div");
        listContainer.className = "documents-viewer-list";
        // Force top alignment — bypass any theme CSS that adds block-start margin
        listContainer.style.cssText = "margin-block-start:0!important;margin-top:0!important;padding-top:0!important;";
        sidebar.appendChild(listContainer);

        // Right viewer panel
        const viewerPanel = document.createElement("div");
        viewerPanel.className = "documents-viewer-panel";

        layout.appendChild(sidebar);
        layout.appendChild(viewerPanel);
        container.replaceWith(layout);

        const isMobile = () => window.matchMedia("(max-width: 767px)").matches;

        let activeDocId = null;

        const showPlaceholder = () => {
          viewerPanel.innerHTML = `<div class="viewer-placeholder">
            <svg viewBox="0 0 64 64" aria-hidden="true">
              <path d="M14 6h25l12 12v36a6 6 0 0 1-6 6H14a6 6 0 0 1-6-6V12a6 6 0 0 1 6-6z" fill="#f0f4f8" stroke="#d0dae6" stroke-width="2"/>
              <path d="M39 6v10a6 6 0 0 0 6 6h10L39 6z" fill="#e5f5fb"/>
              <rect x="18" y="26" width="28" height="2" rx="1.5" fill="#c5d4e0"/>
              <rect x="18" y="32" width="24" height="2" rx="1.5" fill="#c5d4e0"/>
              <rect x="18" y="38" width="20" height="2" rx="1.5" fill="#c5d4e0"/>
            </svg>
            <p>Select a document to preview</p>
          </div>`;
        };

        const loadViewer = (doc) => {
          activeDocId = doc.id;
          const type = doc.type || detectType(doc.link);
          const previewType = getPreviewType(doc.link, type);

          listContainer.querySelectorAll(".viewer-doc-item").forEach((btn) => {
            btn.classList.toggle("is-active", btn.dataset.docId === doc.id);
          });

          viewerPanel.innerHTML = "";

          const content = document.createElement("div");
          content.className = "viewer-content";

          // Header with title + action buttons
          const header = document.createElement("div");
          header.className = "viewer-header";

          const titleEl = document.createElement("span");
          titleEl.className = "viewer-doc-title";
          titleEl.innerHTML = doc.title || "Document";

          const actionsEl = document.createElement("div");
          actionsEl.className = "viewer-actions";

          const openBtn = document.createElement("a");
          openBtn.className = "viewer-download-btn";
          openBtn.href = doc.link || "#";
          openBtn.target = "_blank";
          openBtn.rel = "noopener noreferrer";
          openBtn.innerHTML = `${iconOpen} Open`;

          const downloadBtn = document.createElement("a");
          downloadBtn.className = "viewer-download-btn";
          downloadBtn.href = doc.link || "#";
          downloadBtn.rel = "noopener noreferrer";
          if (isMobile()) {
            downloadBtn.target = "_blank";
          } else {
            downloadBtn.setAttribute("download", "");
          }
          downloadBtn.innerHTML = `${iconDownload} Download`;

          actionsEl.appendChild(openBtn);
          actionsEl.appendChild(downloadBtn);
          header.appendChild(titleEl);
          header.appendChild(actionsEl);
          content.appendChild(header);

          const body = document.createElement("div");
          body.className = "viewer-body";

          if (previewType === "pdf" || previewType === "html") {
            const iframe = document.createElement("iframe");
            iframe.className = "viewer-iframe";
            iframe.src = doc.link;
            iframe.setAttribute("title", doc.title || "Document preview");
            body.appendChild(iframe);
          } else if (previewType === "office") {
            const officeViewUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(doc.link)}`;
            const noPreview = document.createElement("div");
            noPreview.className = "viewer-no-preview";

            const iconEl = document.createElement("div");
            iconEl.className = `doc-icon doc-${(type || "file").toLowerCase()}`;
            iconEl.innerHTML = iconSVG(type || "file");

            const msg = document.createElement("p");
            msg.textContent = "Inline preview is not available for this file type.";

            const officeBtn = document.createElement("a");
            officeBtn.className = "viewer-download-btn-large";
            officeBtn.href = officeViewUrl;
            officeBtn.target = "_blank";
            officeBtn.rel = "noopener noreferrer";
            officeBtn.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M14 3v4a1 1 0 0 0 1 1h4" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" stroke-width="1.8" fill="none"/></svg> Open in Office Online`;

            const dlBtn = document.createElement("a");
            dlBtn.className = "viewer-download-btn";
            dlBtn.href = doc.link || "#";
            dlBtn.setAttribute("download", "");
            dlBtn.rel = "noopener noreferrer";
            dlBtn.innerHTML = `${iconDownload} Download`;

            noPreview.appendChild(iconEl);
            noPreview.appendChild(msg);
            noPreview.appendChild(officeBtn);
            noPreview.appendChild(dlBtn);
            body.appendChild(noPreview);
          } else if (previewType === "image") {
            const img = document.createElement("img");
            img.className = "viewer-image";
            img.src = doc.link;
            img.alt = doc.title || "";
            body.appendChild(img);
          } else {
            const noPreview = document.createElement("div");
            noPreview.className = "viewer-no-preview";

            const iconEl = document.createElement("div");
            iconEl.className = `doc-icon doc-${(type || "file").toLowerCase()}`;
            iconEl.innerHTML = iconSVG(type || "file");

            const msg = document.createElement("p");
            msg.textContent = "No preview available for this file type.";

            const dlBtn = document.createElement("a");
            dlBtn.className = "viewer-download-btn-large";
            dlBtn.href = doc.link || "#";
            dlBtn.setAttribute("download", "");
            dlBtn.rel = "noopener noreferrer";
            dlBtn.innerHTML = `${iconDownload} Download`;

            noPreview.appendChild(iconEl);
            noPreview.appendChild(msg);
            noPreview.appendChild(dlBtn);
            body.appendChild(noPreview);
          }

          content.appendChild(body);
          viewerPanel.appendChild(content);
        };

        const renderViewerList = () => {
          listContainer.innerHTML = "";
          const filtered = getFilteredDocs();

          if (!filtered.length) {
            listContainer.appendChild(createNoResults());
            showPlaceholder();
            return;
          }

          filtered.forEach((doc) => {
            const type = (doc.type || detectType(doc.link)).toLowerCase();
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "viewer-doc-item";
            btn.dataset.docId = doc.id;

            const iconSpan = document.createElement("span");
            if (doc.customIcon) {
              iconSpan.className = "doc-icon doc-custom";
              const img = document.createElement("img");
              img.src = doc.customIcon; img.alt = ""; img.className = "doc-custom-icon";
              iconSpan.appendChild(img);
            } else {
              iconSpan.className = `doc-icon doc-${type}`;
              iconSpan.innerHTML = iconSVG(type);
            }

            const titleSpan = document.createElement("span");
            titleSpan.className = "doc-title";
            titleSpan.innerHTML = doc.title || "";

            btn.appendChild(iconSpan);
            btn.appendChild(titleSpan);

            if (doc.description) {
              const descSpan = document.createElement("span");
              descSpan.className = "doc-description";
              descSpan.innerHTML = doc.description;
              btn.appendChild(descSpan);
            }

            btn.addEventListener("click", () => {
              if (isMobile()) {
                if (doc.link && doc.link !== "#") {
                  window.open(doc.link, "_blank", "noopener,noreferrer");
                }
              } else {
                loadViewer(doc);
              }
            });
            listContainer.appendChild(btn);
          });

          // Restore active selection or auto-select first (desktop only)
          if (!isMobile()) {
            const stillActive = activeDocId && filtered.find((d) => d.id === activeDocId);
            if (stillActive) {
              loadViewer(stillActive);
            } else {
              loadViewer(filtered[0]);
            }
          }
        };

        renderViewerList();

        if (showSearch && searchInput) {
          let debounceTimer;
          searchInput.addEventListener("input", () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
              currentQuery = searchInput.value.trim().toLowerCase();
              renderViewerList();
            }, 150);
          });
        }

        window.addEventListener("hashchange", () => {
          currentLang = getLangFromHash();
          renderViewerList();
        });

        return;
      }

      // ── Normal mode ────────────────────────────────────────────────────────

      let gridBtn, listBtn;

      const controlsRow = document.createElement("div");
      controlsRow.className = "documents-controls";

      if (showSearch) {
        const searchWrapper = document.createElement("div");
        searchWrapper.className = "documents-search";
        const searchSvg = `<svg class="documents-search__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="2"/>
          <path d="M16 16l4.5 4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>`;
        const searchInput = document.createElement("input");
        searchInput.type = "search";
        searchInput.className = "documents-search__input";
        searchInput.placeholder = searchPlaceholder;
        searchInput.setAttribute("aria-label", searchPlaceholder);
        searchWrapper.innerHTML = searchSvg;
        searchWrapper.appendChild(searchInput);
        controlsRow.appendChild(searchWrapper);

        let debounceTimer;
        searchInput.addEventListener("input", () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            currentQuery = searchInput.value.trim().toLowerCase();
            renderDocs(block.dataset.viewmode || initialMode);
          }, 150);
        });
      }

      if (showToggle) {
        const toggleWrapper = document.createElement("div");
        toggleWrapper.classList.add("view-toggle");
        toggleWrapper.setAttribute("role", "toolbar");
        toggleWrapper.setAttribute("aria-label", "View mode");

        listBtn = document.createElement("button");
        listBtn.type = "button";
        listBtn.setAttribute("aria-label", "Liste");
        listBtn.innerHTML = `${iconTable}<span>Liste</span>`;

        gridBtn = document.createElement("button");
        gridBtn.type = "button";
        gridBtn.setAttribute("aria-label", "Cards");
        gridBtn.innerHTML = `${iconCards}<span>Cards</span>`;

        toggleWrapper.appendChild(listBtn);
        toggleWrapper.appendChild(gridBtn);
        controlsRow.appendChild(toggleWrapper);
      }

      if (showSearch || showToggle) {
        if (!block.querySelector(".documents-controls")) {
          block.insertBefore(controlsRow, container);
        }
      }

      const renderDocs = (mode) => {
        const currentMode = (mode || "grid").toLowerCase();
        const classMode = currentMode === "grid" ? "is-grid" : "is-list";
        container.className = `documents-container ${classMode} columns-${columns}`;
        container.innerHTML = "";

        const filtered = getFilteredDocs();

        if (!filtered.length) {
          container.appendChild(createNoResults());
        } else if (currentMode === "grid") {
          filtered.forEach((doc) => container.appendChild(createDocElement(doc, "document-item")));
        } else {
          const listWrapper = document.createElement("div");
          listWrapper.className = "documents-list";
          filtered.forEach((doc) => listWrapper.appendChild(createDocElement(doc, "document-row")));
          container.appendChild(listWrapper);
        }

        if (showToggle && gridBtn && listBtn) {
          const isGrid = currentMode === "grid";
          gridBtn.classList.toggle("active", isGrid);
          listBtn.classList.toggle("active", !isGrid);
          gridBtn.setAttribute("aria-pressed", String(isGrid));
          listBtn.setAttribute("aria-pressed", String(!isGrid));
        }

        block.dataset.viewmode = currentMode;
      };

      renderDocs(initialMode);

      if (showToggle && gridBtn && listBtn) {
        gridBtn.addEventListener("click", () => renderDocs("grid"));
        listBtn.addEventListener("click", () => renderDocs("list"));
      }

      window.addEventListener("hashchange", () => {
        currentLang = getLangFromHash();
        renderDocs(block.dataset.viewmode || initialMode);
      });
    });
  });
})();
